import React, { useMemo, useState } from "react"

import { IMember } from "@cloudike/web_photos/dist/types/intarfaces/IMember"
import { Avatar, Dropdown, Input, List, Tag, Typography } from "antd"
import { MemberRole } from "features/team/model/types/members"
import { getUserRole, userSelector } from "features/user/model/selectors/userSelectors"
import { useAppSelector } from "shared/config/store"
import { DownOutlined, SearchOutlined } from "shared/ui"
import { hasAccess } from "shared/utils/access"
import styled from "styled-components"

const { Text } = Typography

interface ExtendedMember extends IMember {
  isSelectable?: boolean;
  phone_or_email?: string;
  email?: string;
  avatarUrl?: string;
}

interface MemberListProps {
  teamMembers: ExtendedMember[];
  onDelete: (member: ExtendedMember) => void;
  onChangeRole?: (member: ExtendedMember, role: string) => void;
}


export const MemberList: React.FC<MemberListProps> = ({ teamMembers, onDelete, onChangeRole }) => {
  const userRole = useAppSelector(getUserRole)
  const userData = useAppSelector(userSelector)
  const [searchText, setSearchText] = useState<string>("")

  console.log('teamMembers', teamMembers)

  const filteredMembers = useMemo(() => {
    if (!searchText) return teamMembers

    const searchLower = searchText.toLowerCase()
    return teamMembers.filter(member =>
      (member.name?.toLowerCase().includes(searchLower) ||
       member.phone_or_email?.toLowerCase().includes(searchLower) ||
       member.email?.toLowerCase().includes(searchLower))
    )
  }, [teamMembers, searchText])

  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      if (a.role === MemberRole.OWNER) return -1
      if (b.role === MemberRole.OWNER) return 1

      return (a.name || "").localeCompare(b.name || "")
    })
  }, [filteredMembers])


  const renderMemberItem = (member: ExtendedMember) => {
    const isOwner = member.role === 'owner'
    const isSelectable = hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && !isOwner && Number(userData.id) !== Number(member.id)

    const roleMenuItems = [
      {
        key: MemberRole.ADMIN,
        label: 'Admin',
        disabled: isOwner,
        onClick: () => onChangeRole?.(member, MemberRole.ADMIN),
      },
      {
        key: MemberRole.USER,
        label: 'User',
        onClick: () => onChangeRole?.(member, MemberRole.USER),
      },
      {
        key: MemberRole.GUEST,
        label: 'Guest',
        onClick: () => onChangeRole?.(member, MemberRole.GUEST),
      },
      {
        key: 'delete',
        label: 'Delete',
        danger: true,
        onClick: () => onDelete(member),
      },
    ]

    const displayRole = () => {
      switch (member.role) {
      case 'owner':
        return 'Owner'
      case MemberRole.ADMIN:
        return 'Admin'
      case MemberRole.USER:
        return 'User'
      case MemberRole.GUEST:
        return 'Guest'
      default:
        return member.role || 'Guest'
      }
    }

    return (
      <SListItem
        actions={
          isSelectable
            ? [
              <Dropdown
                key="role-dropdown"
                menu={{ items: roleMenuItems }}
                trigger={['click']}
                disabled={isOwner}
              >
                <a onClick={(e) => e.preventDefault()}>
                  <SDropdownTrigger>
                    {displayRole()}

                    <DownOutlined />
                  </SDropdownTrigger>
                </a>
              </Dropdown>
            ]
            : [
              <Text key="role-text"
                type="secondary"
              >
                {displayRole()}
              </Text>
            ]
        }
      >
        <List.Item.Meta
          avatar={(
            <Avatar
              src={member.avatarUrl || undefined}
            >
              {member.name?.[0] || '?'}
            </Avatar>
          )}
          title={(
            <Text strong>
              <span style={{ userSelect: 'text' }}>
                {member.name}
              </span>

              {Number(userData.id) === Number(member.id) && (
                <SYouTag color="purple">
                  me
                </SYouTag>
              )}
            </Text>
          )}
          description={(
            <Text type="secondary"
              style={{ userSelect: 'text' }}
            >
              {member.phone_or_email || member.email || ''}
            </Text>
          )}
        />
      </SListItem>
    )
  }

  return (
    <>
      <SSearchContainer>
        <Input
          placeholder="Search by name or email"
          prefix={<SearchOutlined />}
          onChange={(e) => setSearchText(e.target.value)}
          value={searchText}
        />
      </SSearchContainer>

      <SSectionHeader>
        {`Members (${sortedMembers.length})`}
      </SSectionHeader>

      <List
        itemLayout="horizontal"
        dataSource={sortedMembers}
        renderItem={renderMemberItem}
        locale={{ emptyText: 'No members found' }}
      />
    </>
  )
}

const SSearchContainer = styled.div`
  margin-bottom: 16px;
`

const SSectionHeader = styled.div`
  margin-bottom: 8px;
  padding: 0;
  color: #00000073;
  font-size: 14px;
  font-weight: 600;

  &.mt {
    margin-top: 24px;
  }
`

const SListItem = styled(List.Item)`
  margin-bottom: 8px;
`

const SYouTag = styled(Tag)`
  margin-left: 8px;
  border: 0 ;
  color: var(--ant-color-primary-text-active) !important;
  background-color: var(--ant-color-primary-bg) !important;
`

const SDropdownTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #000000E0;
`

